import type {
  ReportVisual,
  SafetyReportArtifactDetailResponse,
} from '../services/reportsApi';

export type ImpactTimelineStatus =
  | 'interpretable'
  | 'directional_only'
  | 'not_ready'
  | 'waived'
  | 'needs_review'
  | 'insufficient_data';

export type ObservationIntervalStatus = 'monitored' | 'offline' | 'unknown' | 'partial';
export type EventBucketObservationStatus = 'monitored' | 'partial' | 'unmonitored' | 'unknown';

export interface CorrectiveActionImpactTimeline {
  status: ImpactTimelineStatus;
  status_label: string;
  summary: string;
  timeline_mode: 'event_buckets' | 'aggregate_windows' | 'empty';
  report_scope: { start: string | null; end: string | null };
  baseline_window: {
    start: string | null;
    end: string | null;
    elapsed_hours: number | null;
    monitored_hours: number | null;
    event_count: number | null;
    coverage_percent: number | null;
    short_window: boolean;
  };
  corrective_action: {
    pivot_timestamp: string | null;
    pivot_basis: string | null;
    completed_at: string | null;
    action_type: string | null;
    status: string | null;
    label: string;
  };
  followup_window: {
    start: string | null;
    end: string | null;
    elapsed_hours: number | null;
    monitored_hours: number | null;
    event_count: number | null;
    coverage_percent: number | null;
    short_window: boolean;
  };
  event_buckets: Array<{
    bucket_start: string;
    bucket_end: string;
    event_count: number;
    monitored_minutes: number | null;
    rate_per_monitored_hour: number | null;
    observation_status: EventBucketObservationStatus;
    label?: string;
  }>;
  observation_intervals: Array<{
    start: string;
    end: string;
    status: ObservationIntervalStatus;
  }>;
  thresholds: {
    min_baseline_monitored_hours: number;
    min_followup_monitored_hours: number;
    min_bucket_count_for_trend: number;
  };
  readiness: {
    denominator_trust: 'trusted' | 'needs_review' | 'insufficient_data' | 'not_available';
    baseline_ready: boolean;
    followup_ready: boolean;
    trend_ready: boolean;
    effectiveness_verified: boolean;
    reasons: string[];
  };
  interpretation_note: string;
  partial_state_messages: string[];
  y_axis_label: string;
  followup_tone: 'trusted' | 'caution' | 'neutral';
  uses_synthetic_midpoint: boolean;
}

const MIN_MONITORED_HOURS = 2.0;
const MIN_BUCKET_COUNT_FOR_TREND = 5;

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeText(value: unknown): string {
  return String(value ?? '').trim();
}

function splitWindowRange(value?: string | null): { start: string; end: string } {
  const raw = normalizeText(value);
  if (!raw) return { start: '', end: '' };
  const parts = raw.split(/\s+to\s+/i);
  if (parts.length >= 2) {
    return {
      start: normalizeText(parts[0]),
      end: normalizeText(parts.slice(1).join(' to ')),
    };
  }
  return { start: raw, end: '' };
}

function parseTimestamp(value?: string | null): number | null {
  const raw = normalizeText(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) return normalized;
  }
  return '';
}

function visualItemById(visual: ReportVisual | null | undefined, itemId: string) {
  const items = Array.isArray(visual?.data?.items) ? visual?.data?.items || [] : [];
  return items.find((item) => normalizeText(item?.id) === itemId) || null;
}

function parseWindowFromVisual(visual: ReportVisual | null | undefined, itemId: string) {
  const item = visualItemById(visual, itemId);
  return splitWindowRange(normalizeText(item?.ts));
}

function deriveObservationIntervals(
  start: string | null,
  end: string | null,
  elapsedHours: number | null,
  monitoredHours: number | null,
  coveragePercent: number | null,
): Array<{ start: string; end: string; status: ObservationIntervalStatus }> {
  const startMs = parseTimestamp(start);
  const endMs = parseTimestamp(end);
  if (startMs === null || endMs === null || endMs <= startMs) {
    return [];
  }
  const durationMs = endMs - startMs;
  let monitoredRatio: number | null = null;
  if (elapsedHours !== null && elapsedHours > 0 && monitoredHours !== null) {
    monitoredRatio = Math.max(0, Math.min(1, monitoredHours / elapsedHours));
  } else if (coveragePercent !== null) {
    monitoredRatio = Math.max(0, Math.min(1, coveragePercent / 100));
  }
  if (monitoredRatio === null) {
    return [{ start: start!, end: end!, status: 'unknown' }];
  }
  if (monitoredRatio >= 0.999) {
    return [{ start: start!, end: end!, status: 'monitored' }];
  }
  if (monitoredRatio <= 0.001) {
    return [{ start: start!, end: end!, status: 'offline' }];
  }
  const monitoredEndMs = startMs + durationMs * monitoredRatio;
  return [
    { start: start!, end: new Date(monitoredEndMs).toISOString(), status: 'monitored' },
    { start: new Date(monitoredEndMs).toISOString(), end: end!, status: 'offline' },
  ];
}

function isShortMonitoredWindow(monitoredHours: number | null, threshold = MIN_MONITORED_HOURS): boolean {
  return typeof monitoredHours === 'number' && monitoredHours > 0 && monitoredHours < threshold;
}

function buildStatusLabel(status: ImpactTimelineStatus): string {
  switch (status) {
    case 'interpretable':
      return 'Interpretable';
    case 'directional_only':
      return 'Directional only';
    case 'waived':
      return 'Waived';
    case 'needs_review':
      return 'Needs monitored-time review';
    case 'insufficient_data':
    case 'not_ready':
    default:
      return 'Not ready for interpretation';
  }
}

function buildInterpretationNote(props: {
  denominatorTrust: string;
  effectivenessVerified: boolean;
  impactWaived: boolean;
}): string {
  if (props.impactWaived) {
    return 'Impact measurement was waived for this Signed Safety Scan.';
  }
  if (props.effectivenessVerified) {
    return 'Effectiveness verification passed according to retained validation gates.';
  }
  if (props.denominatorTrust === 'trusted') {
    return 'Observed exposure can be compared directionally, but corrective-action effectiveness is not verified.';
  }
  return 'Current event counts are shown for audit context only. Impact interpretation is withheld until monitored-observation time is trusted.';
}

export function buildCorrectiveActionImpactTimeline(input: {
  artifactData?: SafetyReportArtifactDetailResponse | null;
  reportScopeWindow?: { start: string; end: string };
  reportWindowsVisual?: ReportVisual | null;
  impactMeasurementWaived?: boolean;
}): CorrectiveActionImpactTimeline {
  const artifactData = input.artifactData || null;
  const artifact = artifactData?.artifact || null;
  const reportProjection = artifactData?.report_projection || null;
  const technicalDetails = artifactData?.technical_details || null;
  const decisionReadiness = artifactData?.decision_readiness || technicalDetails?.decision_readiness || null;
  const impactGraph = artifactData?.impact_measurement_graph || technicalDetails?.impact_measurement_graph || null;
  const impactSummary = (reportProjection as Record<string, any> | null)?.workflow_state?.impact_summary || null;
  const impactMeasurement = (impactSummary as Record<string, any> | null)?.impact_measurement
    || (impactGraph?.summary as Record<string, any> | undefined)
    || null;
  const effectivenessVerification = decisionReadiness?.effectiveness_verification || null;
  const correctiveAction = decisionReadiness?.corrective_action || null;

  const scopeStart = firstNonEmpty(
    input.reportScopeWindow?.start,
    artifact?.time_window?.start,
  ) || null;
  const scopeEnd = firstNonEmpty(
    input.reportScopeWindow?.end,
    artifact?.time_window?.end,
  ) || null;

  const baselineFromVisual = parseWindowFromVisual(input.reportWindowsVisual, 'baseline_window');
  const followupFromVisual = parseWindowFromVisual(input.reportWindowsVisual, 'followup_window');
  const baselineFromEffectiveness = splitWindowRange(effectivenessVerification?.baseline_window);
  const followupFromEffectiveness = splitWindowRange(effectivenessVerification?.followup_window);

  const baselineStart = firstNonEmpty(
    normalizeText(impactMeasurement?.baseline_window_start),
    normalizeText((impactSummary as Record<string, any> | null)?.baseline_window?.start),
    baselineFromVisual.start,
    baselineFromEffectiveness.start,
  ) || null;
  const baselineEnd = firstNonEmpty(
    normalizeText(impactMeasurement?.baseline_window_end),
    normalizeText((impactSummary as Record<string, any> | null)?.baseline_window?.end),
    baselineFromVisual.end,
    baselineFromEffectiveness.end,
  ) || null;
  const followupStart = firstNonEmpty(
    normalizeText(impactMeasurement?.followup_window_start),
    normalizeText((impactSummary as Record<string, any> | null)?.post_action_window?.start),
    followupFromVisual.start,
    followupFromEffectiveness.start,
  ) || null;
  const followupEnd = firstNonEmpty(
    normalizeText(impactMeasurement?.followup_window_end),
    normalizeText((impactSummary as Record<string, any> | null)?.post_action_window?.end),
    followupFromVisual.end,
    followupFromEffectiveness.end,
  ) || null;

  const baselineMonitoredHours = asFiniteNumber(
    impactMeasurement?.baseline_monitored_hours ?? impactGraph?.summary?.baseline_monitored_hours,
  );
  const followupMonitoredHours = asFiniteNumber(
    impactMeasurement?.followup_monitored_hours ?? impactGraph?.summary?.followup_monitored_hours,
  );
  const baselineElapsedHours = asFiniteNumber(
    impactMeasurement?.baseline_elapsed_hours ?? impactGraph?.summary?.baseline_elapsed_hours,
  );
  const followupElapsedHours = asFiniteNumber(
    impactMeasurement?.followup_elapsed_hours ?? impactGraph?.summary?.followup_elapsed_hours,
  );
  const baselineCoverage = asFiniteNumber(
    impactMeasurement?.baseline_observation_coverage_percent ?? impactGraph?.summary?.baseline_observation_coverage_percent,
  );
  const followupCoverage = asFiniteNumber(
    impactMeasurement?.followup_observation_coverage_percent ?? impactGraph?.summary?.followup_observation_coverage_percent,
  );
  const baselineEventCount = asFiniteNumber(
    impactMeasurement?.baseline_incident_count ?? impactGraph?.summary?.baseline_exposure_count,
  );
  const followupEventCount = asFiniteNumber(
    impactMeasurement?.followup_incident_count ?? impactGraph?.summary?.post_action_exposure_count,
  );

  const denominatorTrustRaw = normalizeText(
    impactMeasurement?.denominator_trust
    ?? impactGraph?.summary?.denominator_trust
    ?? impactGraph?.comparability?.denominator_trust,
  ).toLowerCase();
  const denominatorTrust: CorrectiveActionImpactTimeline['readiness']['denominator_trust'] = (
    denominatorTrustRaw === 'trusted'
    || denominatorTrustRaw === 'needs_review'
    || denominatorTrustRaw === 'insufficient_data'
  ) ? denominatorTrustRaw : 'not_available';

  const measurementStatus = normalizeText(
    impactMeasurement?.measurement_status ?? impactGraph?.summary?.measurement_status,
  ).toLowerCase();
  const impactWaived = Boolean(input.impactMeasurementWaived)
    || normalizeText((impactSummary as Record<string, any> | null)?.status).toLowerCase() === 'waived'
    || measurementStatus === 'waived';

  const pivotBasis = normalizeText(impactMeasurement?.pivot_basis) || null;
  const usesSyntheticMidpoint = pivotBasis === 'synthetic_midpoint_split';
  const pivotTimestamp = firstNonEmpty(
    normalizeText(impactMeasurement?.pivot_timestamp),
    normalizeText(correctiveAction?.completed_at),
  ) || null;
  const completedAt = normalizeText(correctiveAction?.completed_at) || null;
  const actionType = normalizeText(correctiveAction?.action_type) || null;
  const actionStatus = normalizeText(correctiveAction?.status) || null;
  const actionLabel = usesSyntheticMidpoint
    ? 'Corrective-action midpoint (50% split)'
    : actionType
    ? actionType.toLowerCase() === 'training'
      ? 'Training / administrative'
      : actionType.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase())
    : 'Corrective action completed';

  const explicitBuckets = Array.isArray((technicalDetails as Record<string, any> | null)?.event_time_buckets)
    ? ((technicalDetails as Record<string, any>).event_time_buckets as Array<Record<string, unknown>>)
      .map((bucket) => ({
        bucket_start: normalizeText(bucket.bucket_start),
        bucket_end: normalizeText(bucket.bucket_end),
        event_count: asFiniteNumber(bucket.event_count) ?? 0,
        monitored_minutes: asFiniteNumber(bucket.monitored_minutes),
        rate_per_monitored_hour: asFiniteNumber(bucket.rate_per_monitored_hour),
        observation_status: (normalizeText(bucket.observation_status) || 'unknown') as EventBucketObservationStatus,
        label: normalizeText(bucket.label) || undefined,
      }))
      .filter((bucket) => bucket.bucket_start && bucket.bucket_end)
    : [];

  const aggregateBuckets: CorrectiveActionImpactTimeline['event_buckets'] = [];
  if (baselineStart && baselineEnd && baselineEventCount !== null) {
    aggregateBuckets.push({
      bucket_start: baselineStart,
      bucket_end: baselineEnd,
      event_count: baselineEventCount,
      monitored_minutes: baselineMonitoredHours !== null ? baselineMonitoredHours * 60 : null,
      rate_per_monitored_hour: denominatorTrust === 'trusted' && baselineMonitoredHours && baselineMonitoredHours > 0
        ? Number((baselineEventCount / baselineMonitoredHours).toFixed(2))
        : null,
      observation_status: baselineMonitoredHours !== null && baselineElapsedHours !== null && baselineMonitoredHours < baselineElapsedHours
        ? 'partial'
        : baselineMonitoredHours && baselineMonitoredHours > 0
        ? 'monitored'
        : 'unknown',
      label: 'Baseline window',
    });
  }
  if (followupStart && followupEnd && followupEventCount !== null) {
    aggregateBuckets.push({
      bucket_start: followupStart,
      bucket_end: followupEnd,
      event_count: followupEventCount,
      monitored_minutes: followupMonitoredHours !== null ? followupMonitoredHours * 60 : null,
      rate_per_monitored_hour: denominatorTrust === 'trusted' && followupMonitoredHours && followupMonitoredHours > 0
        ? Number((followupEventCount / followupMonitoredHours).toFixed(2))
        : null,
      observation_status: followupMonitoredHours !== null && followupElapsedHours !== null && followupMonitoredHours < followupElapsedHours
        ? 'partial'
        : followupMonitoredHours && followupMonitoredHours > 0
        ? 'monitored'
        : 'unknown',
      label: 'Follow-up window',
    });
  }

  const eventBuckets = explicitBuckets.length >= MIN_BUCKET_COUNT_FOR_TREND
    ? explicitBuckets
    : aggregateBuckets;

  const timelineMode: CorrectiveActionImpactTimeline['timeline_mode'] = explicitBuckets.length >= MIN_BUCKET_COUNT_FOR_TREND
    ? 'event_buckets'
    : aggregateBuckets.length
    ? 'aggregate_windows'
    : 'empty';

  const observationIntervals = [
    ...deriveObservationIntervals(baselineStart, baselineEnd, baselineElapsedHours, baselineMonitoredHours, baselineCoverage),
    ...deriveObservationIntervals(followupStart, followupEnd, followupElapsedHours, followupMonitoredHours, followupCoverage),
  ];

  const baselineReady = baselineMonitoredHours !== null && baselineMonitoredHours >= MIN_MONITORED_HOURS;
  const followupReady = followupMonitoredHours !== null && followupMonitoredHours >= MIN_MONITORED_HOURS;
  const trendReady = explicitBuckets.length >= MIN_BUCKET_COUNT_FOR_TREND;
  const effectivenessVerified = normalizeText(effectivenessVerification?.status).toLowerCase() === 'verified';

  const reasons: string[] = [];
  if (!baselineReady) reasons.push('Baseline monitored time below recommended threshold');
  if (!followupReady) reasons.push('Follow-up monitored time below recommended threshold');
  if (!trendReady) reasons.push('Insufficient event-time buckets for trend interpretation');
  if (denominatorTrust !== 'trusted') reasons.push('Monitored-observation time is not trusted');
  if (!effectivenessVerified) reasons.push('Effectiveness is not verified');

  let status: ImpactTimelineStatus = 'not_ready';
  if (impactWaived) {
    status = 'waived';
  } else if (denominatorTrust === 'needs_review' || denominatorTrust === 'insufficient_data') {
    status = denominatorTrust === 'insufficient_data' ? 'insufficient_data' : 'needs_review';
  } else if (denominatorTrust === 'trusted' && (measurementStatus === 'available' || measurementStatus === 'measured')) {
    status = 'interpretable';
  } else if (denominatorTrust === 'trusted' && measurementStatus === 'directional') {
    status = 'directional_only';
  }

  const partialStateMessages: string[] = [];
  if (timelineMode === 'empty') {
    partialStateMessages.push('Timeline cannot be rendered because event-time data is not included in the signed-off package.');
  } else if (timelineMode === 'aggregate_windows') {
    partialStateMessages.push('Event-time buckets are not included in this signed-off package.');
  }
  if (!pivotTimestamp) {
    partialStateMessages.push('Corrective-action pivot is not available. Baseline/follow-up timeline cannot be fully interpreted.');
  }
  if (!observationIntervals.length) {
    partialStateMessages.push('Observation coverage intervals are not included. Impact interpretation may require monitored-time review.');
  }

  const summary = normalizeText(impactGraph?.summary?.measurement_notes?.[0])
    || normalizeText((impactSummary as Record<string, any> | null)?.display_summary)
    || normalizeText(effectivenessVerification?.summary)
    || 'Signed-off corrective-action impact context retained in the finalized package.';

  return {
    status,
    status_label: buildStatusLabel(status),
    summary,
    timeline_mode: timelineMode,
    report_scope: { start: scopeStart, end: scopeEnd },
    baseline_window: {
      start: baselineStart,
      end: baselineEnd,
      elapsed_hours: baselineElapsedHours,
      monitored_hours: baselineMonitoredHours,
      event_count: baselineEventCount,
      coverage_percent: baselineCoverage,
      short_window: isShortMonitoredWindow(baselineMonitoredHours),
    },
    corrective_action: {
      pivot_timestamp: pivotTimestamp,
      pivot_basis: pivotBasis,
      completed_at: completedAt,
      action_type: actionType,
      status: actionStatus,
      label: actionLabel,
    },
    followup_window: {
      start: followupStart,
      end: followupEnd,
      elapsed_hours: followupElapsedHours,
      monitored_hours: followupMonitoredHours,
      event_count: followupEventCount,
      coverage_percent: followupCoverage,
      short_window: isShortMonitoredWindow(followupMonitoredHours),
    },
    event_buckets: eventBuckets,
    observation_intervals: observationIntervals,
    thresholds: {
      min_baseline_monitored_hours: MIN_MONITORED_HOURS,
      min_followup_monitored_hours: MIN_MONITORED_HOURS,
      min_bucket_count_for_trend: MIN_BUCKET_COUNT_FOR_TREND,
    },
    readiness: {
      denominator_trust: denominatorTrust,
      baseline_ready: baselineReady,
      followup_ready: followupReady,
      trend_ready: trendReady,
      effectiveness_verified: effectivenessVerified,
      reasons,
    },
    interpretation_note: buildInterpretationNote({
      denominatorTrust,
      effectivenessVerified,
      impactWaived,
    }),
    partial_state_messages: partialStateMessages,
    y_axis_label: denominatorTrust === 'trusted' ? 'Rate per monitored hour' : 'Event count (audit context)',
    followup_tone: denominatorTrust === 'trusted' && !impactWaived ? 'trusted' : 'caution',
    uses_synthetic_midpoint: usesSyntheticMidpoint,
  };
}

export function buildImpactTimelineChartPoints(timeline: CorrectiveActionImpactTimeline) {
  return timeline.event_buckets.map((bucket, index) => {
    const startMs = parseTimestamp(bucket.bucket_start) ?? index;
    const endMs = parseTimestamp(bucket.bucket_end) ?? startMs + 1;
    const midpoint = startMs + (endMs - startMs) / 2;
    return {
      id: `${bucket.label || 'bucket'}-${index}`,
      label: bucket.label || `Window ${index + 1}`,
      startMs,
      endMs,
      midpoint,
      event_count: bucket.event_count,
      rate_per_monitored_hour: bucket.rate_per_monitored_hour,
      bucket_start: bucket.bucket_start,
      bucket_end: bucket.bucket_end,
      observation_status: bucket.observation_status,
    };
  });
}
